import { Client } from '@app/core/types/client';
import { User } from '@mi-front/core';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Box, Tooltip } from '@mui/material';
import { HTMLAttributes } from 'react';
import { useIntl } from 'react-intl';
import { impersonationMessages } from '../impersonation-messages';
import { ImpersonationOpenInNewTabButton } from './ImpersonationOpenInNewTabButton';
import { ImpersonationUserListItem } from './ImpersonationUserListItem';

interface ImpersonationAutocompleteOptionProps {
  optionProps: HTMLAttributes<HTMLLIElement>;
  user: User & { client: Client };
  /** When true, shows the Enter hint (first match while filtering). */
  showEnterHint: boolean;
  /** Opens tab-scoped impersonation in a new browser tab. */
  onOpenInNewTab: (user: User & { client: Client }) => void;
}

/**
 * Autocomplete option row; Enter/click persists, OpenInNew opens tab-only impersonation.
 */
export function ImpersonationAutocompleteOption({
  optionProps,
  user,
  showEnterHint,
  onOpenInNewTab,
}: ImpersonationAutocompleteOptionProps) {
  const { $t } = useIntl();

  return (
    <ImpersonationUserListItem
      {...optionProps}
      user={user}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          ml: 'auto',
          flexShrink: 0,
        }}
      >
        {showEnterHint ? (
          <Tooltip title={$t(impersonationMessages.enterToSelect)}>
            <KeyboardReturnIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
              }}
            />
          </Tooltip>
        ) : null}
        <ImpersonationOpenInNewTabButton onClick={() => onOpenInNewTab(user)} />
      </Box>
    </ImpersonationUserListItem>
  );
}
